/**
 * Reviews Service
 * Handles review-related API operations
 */

import api from './api';

class ReviewsService {
  /**
   * Create a new review for a completed job and driver
   * @param {Object} reviewData - Review data
   * @param {string} reviewData.driver - Driver ID
   * @param {string} reviewData.job - Job ID
   * @param {number} reviewData.rating - Rating (1-5)
   * @param {string} reviewData.comment - Optional comment
   * @returns {Promise<Object>} Created review
   */
  async createReview(reviewData) {
    const response = await api.post('/review', reviewData);

    if (response.success && response.data) {
      return response.data.review;
    }

    throw new Error(response.message || 'Failed to create review');
  }

  /**
   * Get review for a specific job
   * @param {string} jobId - Job ID
   * @returns {Promise<Object|null>} Review if exists, null otherwise
   */
  async getReviewByJob(jobId) {
    try {
      const response = await api.get(`/review?job=${jobId}`);

      if (response.success && response.data) {
        const reviews = response.data.reviews || response.data.items || [];
        return reviews.length > 0 ? reviews[0] : null;
      }

      return null;
    } catch (error) {
      // If 404 or no review found, return null
      return null;
    }
  }

  /**
   * Get reviews for a specific driver
   * @param {string} driverId - Driver ID
   * @returns {Promise<Object>} Reviews list with pagination
   */
  async getDriverReviews(driverId) {
    const response = await api.get(`/review/driver/${driverId}`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch driver reviews');
  }

  /**
   * Get review statistics
   * @returns {Promise<Object>} Review statistics
   */
  async getReviewStats() {
    const response = await api.get('/review/stats');

    if (response.success && response.data) {
      return response.data.stats;
    }

    throw new Error('Failed to fetch review statistics');
  }

  /**
   * Update a review
   * @param {string} reviewId - Review ID
   * @param {Object} updates - Updated data
   * @returns {Promise<Object>} Updated review
   */
  async updateReview(reviewId, updates) {
    const response = await api.put(`/review/${reviewId}`, updates);

    if (response.success && response.data) {
      return response.data.review;
    }

    throw new Error('Failed to update review');
  }

  /**
   * Delete a review
   * @param {string} reviewId - Review ID
   * @returns {Promise<Object>}
   */
  async deleteReview(reviewId) {
    const response = await api.delete(`/review/${reviewId}`);

    return response;
  }
}

const reviewsService = new ReviewsService();
export default reviewsService;
